import axios from 'axios';
import { apiClient } from './client';

/**
 * S3 presigned URL helpers. Backend endpoint:
 *   POST /api/v1/s3/presigned-url
 *   Body: { type: "upload" | "download" | "delete", s3_keys: string[] }
 *   Response: { data: { s3_urls: { type, urls: [{ s3_key, url }] }}}
 */

interface PresignedResponse {
  data: {
    s3_urls: {
      type: 'upload' | 'download' | 'delete';
      urls: Array<{ s3_key: string; url: string }>;
    };
  };
}

/**
 * Fetch a short-lived download URL for an S3 object (e.g. a citation PDF).
 * Thin wrapper around the internal helper so UI components don't need to
 * know the "upload" | "download" discriminator.
 *
 * Optional `filename` appends a `response-content-disposition` query param
 * so the returned URL, when opened in Word or a browser, uses a readable
 * filename instead of the opaque S3 key.
 */
export async function getPresignedDownloadUrl(
  s3Key: string,
  opts: { filename?: string } = {},
): Promise<string> {
  const url = await getPresignedUrl('download', s3Key);
  if (!opts.filename) return url;
  const sep = url.includes('?') ? '&' : '?';
  const disposition = `attachment; filename="${opts.filename.replace(/"/g, '')}"`;
  return `${url}${sep}response-content-disposition=${encodeURIComponent(disposition)}`;
}

async function getPresignedUrl(type: 'upload' | 'download', s3Key: string): Promise<string> {
  const res = await apiClient.post<unknown>('/api/v1/s3/presigned-url', {
    type,
    s3_keys: [s3Key],
  });
  // Backend wraps responses in ServerResponse { success, status_code, message, data }.
  const body = (res.data as Partial<PresignedResponse>) ?? {};
  const urls = body.data?.s3_urls?.urls ?? [];
  if (!urls[0]?.url) throw new Error('Presigned URL response was empty');
  return urls[0].url;
}

/**
 * Upload DOCX bytes to S3 via presigned URL.
 * Uses bare fetch (not apiClient) to avoid Authorization header leaking to S3.
 */
export async function uploadDocxToS3(
  bytes: Uint8Array,
  s3Key: string,
  onProgress?: (percent: number) => void,
): Promise<void> {
  const url = await getPresignedUrl('upload', s3Key);
  await axios.put(url, bytes, {
    headers: {
      'Content-Type':
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    },
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
    },
    // axios defaults to JSON; we need raw bytes through
    transformRequest: [(data) => data],
  });
}

/**
 * Download DOCX bytes from S3 via presigned URL.
 */
export async function downloadDocxFromS3(s3Key: string): Promise<ArrayBuffer> {
  const url = await getPresignedUrl('download', s3Key);
  const res = await axios.get<ArrayBuffer>(url, { responseType: 'arraybuffer' });
  return res.data;
}
